import { ApolloClient, from, InMemoryCache, HttpLink } from '@apollo/client';
import { useMsal } from '@azure/msal-react';
import { setContext } from 'apollo-link-context';
import { removeTypenameFromMutationLink } from 'apollo-remove-typename-mutation-link';
import buildGraphQLProvider, { buildQuery } from 'ra-data-graphql-simple';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from 'ra-language-english';
import { useEffect, useState } from 'react';
import { QueryClient } from 'react-query';
import { getAccessToken } from './msal/MsalConfig';
import * as domainMessages from './Translation/domainTranslation';
import welshMessages from './Translation/Welsh';
import { gql } from '@apollo/client';

export const useApolloClientDataProvider = () => {
    const [dataProvider, setDataProvider] = useState(null);
    const [client, setClient] = useState(null);
    const { instance: msalInstance } = useMsal();

    useEffect(() => {
        const httpLink = new HttpLink({
            uri: process.env.REACT_APP_GRAPHQL_URL,
        });
        const authLink = setContext(async (_, { headers }) => {
            const token = await getAccessToken(msalInstance);
            return {
                headers: {
                    ...headers,
                    authorization: token ? `Bearer ${token}` : '',
                    'x-graph-key': process.env.REACT_APP_GRAPHQL_KEY,
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Methods': '*',
                    'Access-Control-Allow-Headers': 'Content-Type'
                },
            };
        });
        const link = from([removeTypenameFromMutationLink, authLink, httpLink]);

        const inMemoryCache = new InMemoryCache({ addTypename: true });
        const client = new ApolloClient({
            link,
            cache: inMemoryCache,
            resultCaching: true,
            addTypename: true,
        });

        console.log('Connecting to ' + process.env.REACT_APP_GRAPHQL_URL);

                // Build custom query logic
                const myBuildQuery = introspection => (fetchType, resource, params) => {
                    const builtQuery = buildQuery(introspection)(fetchType, resource, params);
                
                    if (resource === 'PDFSpec' && fetchType === 'GET_ONE') {
                        return {
                            // Use the default query variables and parseResponse
                            ...builtQuery,
                            // Override the query
                            query: gql`
                                query PDFSpec($id: ID!) {
                                    data: PDFSpec(id: $id) {
                                        id
                                        version
                                    }
                                }`,
                        };
                    }
                
                    return builtQuery;
                };

        buildGraphQLProvider({ client: client, buildQuery: myBuildQuery}).then((dataProvider) => {
            setDataProvider(dataProvider);
            setClient(client);
        });
        console.log('built graphQL provider');
    }, []);

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 3 * 60 * 60 * 1000, // 3 hours 
            },
        },
    });

    const i18nProvider = polyglotI18nProvider((locale) => {
        if (locale === 'en') {
            console.log('Speaking English');
            let enTranslation = { ...englishMessages, ...domainMessages.en };
            enTranslation.ra.message.invalid_form =
                'Please check the forms mandatory fields are complete';

            return enTranslation;
        } else if (locale === 'cy') {
            console.log('Speaking Welsh');
            const welshTranslation = { ...welshMessages, ...domainMessages.cy };
            welshTranslation.ra.message.invalid_form = 
                `Gwiriwch fod meysydd gorfodol y ffurflenni wedi'u cwblhau`;

            return welshTranslation;
        } else {
            console.log('Unrecognised locale.  Defaulting to English');
            return { ...englishMessages, ...domainMessages.en };
        }
    }, 'en');

    return [client, dataProvider, i18nProvider, queryClient];
};
